/**
 * ConversationThread — the one conversation view (assistant-ui) every chat surface renders inside `ChatPage`: the
 * messages scroll, the composer stays pinned at the bottom. Demo page: /chats (open conversation: incoming bubbles on
 * the left, the person's on the right in the brand colour, time under each, composer with send at the bottom).
 *
 * The runtime comes from `useConversationRuntime(stream)`; non-text Conflui parts render through `renderPart`, so each
 * surface keeps its existing part renderers (cards, proposals, interaction requests, tables).
 */
import {
  type AssistantRuntime,
  AssistantRuntimeProvider,
  ComposerPrimitive,
  ThreadPrimitive,
  useAuiEvent,
} from '@assistant-ui/react';
import { ArrowDown, Paperclip, SendHorizontal } from 'lucide-react';
import type {
  ChangeEventHandler,
  ComponentType,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
  UIEvent,
} from 'react';
import { cn } from '../../utils/cn.js';
import { AssistantMessage, SystemMessage, UserMessage } from './thread-messages.js';
import { type ConversationPartRenderer, type ThreadRenderers, ThreadRenderersProvider } from './thread-parts.js';

/** What a surface may set on the composer input. */
export interface ComposerInputOptions {
  /** Runs before the thread's own Enter-to-send; `preventDefault()` stops the send (slash-command keys). */
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  /** `enter` (default), `ctrlEnter`, or `none` when the surface sends itself. */
  submitMode?: 'enter' | 'ctrlEnter' | 'none';
}

/** How near the top (px) the scroll position counts as "reached the top". */
const TOP_THRESHOLD_PX = 48;

export interface ConversationThreadProps {
  runtime: AssistantRuntime;
  /** Renders one non-text Conflui part (card, proposal, interaction request, table, …). */
  renderPart: ConversationPartRenderer;
  /** Replaces a message's whole body for rows the surface draws itself (a parsed system row, a raw tool row). */
  renderMessage?: ThreadRenderers['renderMessage'];
  /** Under a message, beside its time: intent badge, token counts, a raw-view toggle, who it is for. */
  renderMessageFooter?: ThreadRenderers['renderMessageFooter'];
  /** Above a message: who wrote it (a member's name and kind). */
  renderMessageHeader?: ThreadRenderers['renderMessageHeader'];
  /** A person's message written by someone else: drawn as incoming, under its writer's name. */
  isIncoming?: ThreadRenderers['isIncoming'];
  /** Beside a tool call's name: a panel link, the duration. */
  renderToolCallExtra?: ThreadRenderers['renderToolCallExtra'];
  /**
   * Files in messages (`ThreadRenderers.messageAttachments`), and the chip of a file being attached in the composer. With
   * the chip the composer offers attaching — a paperclip, dropping files on it, pasting an image — through the runtime's
   * attachments adapter.
   */
  messageAttachments?: ThreadRenderers['messageAttachments'];
  composerAttachment?: ComponentType;
  /** A file refused before it became a chip (its type is not one the attachments adapter takes): the surface says so. */
  onAttachmentRefused?: () => void;
  /** Called when the person scrolls to the top (load older messages). */
  onReachTop?: () => void;
  /** What an empty conversation shows (an `EmptyState`, starter suggestions). */
  empty?: ReactNode;
  /** At the top of the scroll area, above the first message ("Load earlier messages"). */
  header?: ReactNode;
  /** Left of the input: attach, slash commands, the policy picker. */
  composerStart?: ReactNode;
  /** Above the input row: the composer's own panels (slash-command suggestions, chosen attachments). */
  composerPanel?: ReactNode;
  /**
   * Props for the input: `onKeyDown` runs before the thread's own Enter-to-send and stops it with `preventDefault()`
   * (slash-command keys), `onChange` sees each edit.
   */
  composerInputProps?: ComposerInputOptions;
  /** Under the input row: a standing note ("Deployments always need a second approver"). */
  composerFooter?: ReactNode;
  placeholder?: string;
  /** Set when the person cannot write here (archived, no permission): replaces the composer. */
  readOnlyNote?: ReactNode;
  className?: string;
}

const MESSAGE_COMPONENTS = { UserMessage, AssistantMessage, SystemMessage };

/**
 * Reports a file the composer refused before any chip existed — a picked, dropped or pasted file of a type the adapter
 * does not take; nothing would show otherwise. A refusal after the chip exists is the chip's own (`adapter-error`).
 */
function AttachmentRefusals({ onRefused }: { onRefused: () => void }) {
  useAuiEvent('composer.attachmentAddError', (event) => {
    if (event.reason !== 'adapter-error') onRefused();
  });
  return null;
}

export function ConversationThread({
  runtime,
  renderPart,
  renderMessage,
  renderMessageFooter,
  renderMessageHeader,
  isIncoming,
  renderToolCallExtra,
  messageAttachments,
  composerAttachment,
  onAttachmentRefused,
  onReachTop,
  empty,
  header,
  composerStart,
  composerPanel,
  composerInputProps,
  composerFooter,
  placeholder = 'Write a message…',
  readOnlyNote,
  className,
}: ConversationThreadProps) {
  const renderers: ThreadRenderers = {
    renderPart,
    renderMessage,
    renderMessageFooter,
    renderMessageHeader,
    isIncoming,
    renderToolCallExtra,
    messageAttachments,
  };
  const onScroll = onReachTop
    ? (event: UIEvent<HTMLDivElement>) => {
        if (event.currentTarget.scrollTop < TOP_THRESHOLD_PX) onReachTop();
      }
    : undefined;
  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ThreadRenderersProvider value={renderers}>
        <ThreadPrimitive.Root className={cn('flex h-full min-h-0 flex-col', className)}>
          <ThreadPrimitive.Viewport
            className="relative flex min-h-0 flex-1 flex-col overflow-y-auto"
            onScroll={onScroll}
          >
            <div className="flex flex-1 flex-col gap-5 px-4 py-5 sm:px-6">
              {header}
              <ThreadPrimitive.Empty>
                <div className="flex flex-1 items-center justify-center py-10">{empty}</div>
              </ThreadPrimitive.Empty>
              <ThreadPrimitive.Messages components={MESSAGE_COMPONENTS} />
            </div>
            <ThreadPrimitive.ViewportFooter className="sticky bottom-0 flex flex-col items-center bg-surface">
              <ThreadPrimitive.ScrollToBottom
                className="absolute -top-12 rounded-full border border-card-border bg-surface p-2 text-text-secondary shadow-sm transition-opacity disabled:invisible disabled:opacity-0"
                aria-label="Scroll to the latest message"
              >
                <ArrowDown size={16} aria-hidden />
              </ThreadPrimitive.ScrollToBottom>
              <div className="w-full border-t border-card-border px-4 py-3 sm:px-6">
                {readOnlyNote ? (
                  <p className="py-2 text-center text-sm text-text-tertiary">{readOnlyNote}</p>
                ) : (
                  <ComposerPrimitive.Root className="flex flex-col gap-2">
                    {composerAttachment && onAttachmentRefused && (
                      <AttachmentRefusals onRefused={onAttachmentRefused} />
                    )}
                    {composerPanel}
                    {composerAttachment && (
                      <div className="flex flex-wrap gap-2 empty:hidden">
                        <ComposerPrimitive.Attachments components={{ Attachment: composerAttachment }} />
                      </div>
                    )}
                    <ComposerPrimitive.AttachmentDropzone
                      disabled={!composerAttachment}
                      className="flex items-end gap-2 rounded-xl border border-card-border bg-input-background px-3 py-2 focus-within:border-input-primary-focus-border focus-within:ring-4 focus-within:ring-input-primary-focus-border/20 data-[dragging=true]:border-input-primary-focus-border data-[dragging=true]:bg-input-primary-focus-border/10"
                    >
                      {composerAttachment && (
                        <ComposerPrimitive.AddAttachment
                          className="flex size-8 shrink-0 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-surface-alt hover:text-text-primary disabled:opacity-40"
                          aria-label="Attach files"
                        >
                          <Paperclip size={16} aria-hidden />
                        </ComposerPrimitive.AddAttachment>
                      )}
                      {composerStart}
                      <ComposerPrimitive.Input
                        submitMode="enter"
                        aria-label="Message"
                        {...composerInputProps}
                        rows={1}
                        placeholder={placeholder}
                        className="max-h-48 min-h-6 flex-1 resize-none border-0 bg-transparent px-0 py-1 text-sm text-text-primary shadow-none outline-none placeholder:text-input-placeholder-text focus:ring-0 focus:outline-none"
                      />
                      <ComposerPrimitive.Send
                        className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-button-primary-background text-button-primary-text transition-colors hover:bg-button-primary-hover-background disabled:opacity-40"
                        aria-label="Send"
                      >
                        <SendHorizontal size={16} aria-hidden />
                      </ComposerPrimitive.Send>
                    </ComposerPrimitive.AttachmentDropzone>
                    {composerFooter && <p className="px-1 text-xs text-text-tertiary">{composerFooter}</p>}
                  </ComposerPrimitive.Root>
                )}
              </div>
            </ThreadPrimitive.ViewportFooter>
          </ThreadPrimitive.Viewport>
        </ThreadPrimitive.Root>
      </ThreadRenderersProvider>
    </AssistantRuntimeProvider>
  );
}
